/**
 * Vidu S1 Live embed — table talk (BUDB) or blackjack dealer (Racalvin).
 *
 * Surfaces (only):
 *  - /blackjack → Racalvin
 *  - /table/hu-vs-budb → BetUDontBet
 *
 * Player open flow (any platform):
 *  1) Full-screen accept popup with mode selection
 *  2) User gesture → mic permission → Worker /api/vidu/live → voice↔voice
 * Never auto-starts mic without accept (iOS + privacy).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  resolveViduPersona,
  type ViduPersonaId,
} from "@shared/vidu-personas";
import {
  connectViduControl,
  createViduLiveSession,
  tryJoinAliRtc,
  type ViduLiveController,
} from "@/lib/vidu-live";
import {
  detectVoicePlatform,
  hasVoiceConsent,
  openVoiceMicForAi,
  saveVoiceConsent,
  voicePlatformTips,
} from "@/lib/media-devices";
import {
  ExternalLink,
  Minimize2,
  Maximize2,
  Radio,
  Mic,
  MicOff,
  Headphones,
  ShieldCheck,
  MessageSquare,
  Volume2,
} from "lucide-react";

export const VIDU_CHAT_EVENT = "vidu-persona-chat";

/** Relay table/BJ chat → active persona Live text_msg */
export function relayChatToViduPersona(
  text: string,
  persona?: ViduPersonaId | string,
) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(VIDU_CHAT_EVENT, {
      detail: { text, persona: persona || "budb" },
    }),
  );
}

/** Back-compat alias used by table chat */
export const BUDB_VIDU_CHAT_EVENT = "budb-vidu-chat";
export function relayChatToBudbVidu(text: string) {
  relayChatToViduPersona(text, "budb");
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(BUDB_VIDU_CHAT_EVENT, { detail: { text } }),
    );
  }
}

const THEME: Record<
  string,
  { border: string; header: string; title: string; accent: string; soft: string }
> = {
  budb: {
    border: "rgba(234,179,8,0.5)",
    header: "linear-gradient(90deg,rgba(234,179,8,0.2),transparent)",
    title: "#fde68a",
    accent: "#eab308",
    soft: "rgba(234,179,8,0.1)",
  },
  racalvin: {
    border: "rgba(52,211,153,0.55)",
    header: "linear-gradient(90deg,rgba(16,185,129,0.25),transparent)",
    title: "#a7f3d0",
    accent: "#34d399",
    soft: "rgba(16,185,129,0.1)",
  },
};

type LiveStatus =
  | "idle"
  | "gate"
  | "creating"
  | "connecting"
  | "live"
  | "fallback"
  | "error";

/** Player-selected open mode */
type VoiceOpenMode = "voice" | "face" | "later";

type GateOption = {
  id: VoiceOpenMode;
  title: string;
  desc: string;
  icon: "mic" | "face" | "later";
};

function gateOptions(personaId: string): GateOption[] {
  const who =
    personaId === "racalvin" ? "Racalvin (blackjack dealer)" : "BetUDontBet";
  return [
    {
      id: "voice",
      title: "Voice ↔ voice",
      desc: `Talk with ${who}. Needs microphone. Best with headphones.`,
      icon: "mic",
    },
    {
      id: "face",
      title: "Face only",
      desc: "See the AI face and use typed chat. No microphone.",
      icon: "face",
    },
    {
      id: "later",
      title: "Not now",
      desc: "Minimize. Re-open anytime with the voice button.",
      icon: "later",
    },
  ];
}

export function ViduPersonaEmbed(props: {
  persona?: ViduPersonaId | string;
  compact?: boolean;
  className?: string;
  /**
   * Show panel; if no prior consent, show accept popup.
   * Never starts mic without player acceptance.
   */
  autoStart?: boolean;
  position?: "fixed" | "absolute" | "relative";
}) {
  const persona = resolveViduPersona(props.persona);
  const theme = THEME[persona.id] || THEME.budb!;
  const platform = detectVoicePlatform();
  const tips = voicePlatformTips();
  const options = gateOptions(persona.id);

  const [open, setOpen] = useState(true);
  const [consented, setConsented] = useState(() =>
    hasVoiceConsent(persona.id as "budb" | "racalvin"),
  );
  const [status, setStatus] = useState<LiveStatus>(() =>
    hasVoiceConsent(persona.id as "budb" | "racalvin") ? "idle" : "gate",
  );
  const [err, setErr] = useState<string | null>(null);
  const [liveId, setLiveId] = useState<string | null>(null);
  const [busyAccept, setBusyAccept] = useState(false);
  const [selectedMode, setSelectedMode] = useState<VoiceOpenMode>("voice");
  const [workerOk, setWorkerOk] = useState<boolean | null>(null);
  const ctrlRef = useRef<ViduLiveController | null>(null);
  const leaveRtcRef = useRef<(() => void) | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const startedRef = useRef(false);
  const autoStart = props.autoStart !== false;
  const pos = props.position || "fixed";

  const destroy = useCallback(() => {
    startedRef.current = false;
    try {
      ctrlRef.current?.destroy();
    } catch {
      /* ignore */
    }
    ctrlRef.current = null;
    try {
      leaveRtcRef.current?.();
    } catch {
      /* ignore */
    }
    leaveRtcRef.current = null;
  }, []);

  // Prefetch Worker Vidu health (no secret exposure)
  useEffect(() => {
    let cancelled = false;
    void fetch("/api/vidu/status")
      .then((r) => r.json())
      .then((j: { configured?: boolean; ok?: boolean }) => {
        if (!cancelled) setWorkerOk(Boolean(j?.configured ?? j?.ok));
      })
      .catch(() => {
        if (!cancelled) setWorkerOk(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startLive = useCallback(async () => {
    destroy();
    startedRef.current = true;
    setErr(null);
    setStatus("creating");
    try {
      // Mic unlock (idempotent if already granted this gesture)
      const mic = await openVoiceMicForAi();
      if (!mic.ok) {
        if (mic.denied) {
          setErr(
            "Microphone blocked. Allow mic for poker.grudge-studio.com in browser settings, then Retry.",
          );
        } else if (mic.unsupported) {
          setErr(mic.error || "Voice not supported in this browser");
        } else {
          setErr(mic.error || "Could not open microphone");
        }
        setStatus("fallback");
        return;
      }

      let session;
      try {
        session = await createViduLiveSession({
          callMode: "audio",
          persona: persona.id,
        });
      } catch (e1) {
        // One soft retry for transient edge failures
        await new Promise((r) => setTimeout(r, 450));
        try {
          session = await createViduLiveSession({
            callMode: "audio",
            persona: persona.id,
          });
        } catch {
          throw e1;
        }
      }

      setLiveId(session.liveId);
      setStatus("connecting");

      const ctrl = connectViduControl(session, {
        onReady: () => setStatus("live"),
        onError: (e) => {
          setErr(e);
          setStatus((s) => (s === "live" ? s : "fallback"));
        },
        onClose: () => {
          if (ctrlRef.current) setStatus("fallback");
        },
      });
      ctrlRef.current = ctrl;

      if (session.rtc && videoRef.current) {
        const rtc = await tryJoinAliRtc(session.rtc, videoRef.current);
        if (rtc.ok && rtc.leave) leaveRtcRef.current = rtc.leave;
      }

      const greetTimer = window.setInterval(() => {
        if (ctrl.ready) {
          ctrl.sendText(
            persona.id === "racalvin"
              ? "Player enabled voice on blackjack. Greet short as Racalvin the dealer — invite them to talk."
              : "Player enabled voice on HU VS BUDB. Greet short as BetUDontBet — invite voice or typed chat.",
          );
          window.clearInterval(greetTimer);
        }
      }, 700);
      window.setTimeout(() => window.clearInterval(greetTimer), 18000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setStatus("fallback");
    }
  }, [destroy, persona.id]);

  /** Confirm selection from popup (must run on click — user gesture). */
  const confirmOpen = useCallback(async () => {
    if (selectedMode === "later") {
      setOpen(false);
      setStatus("idle");
      destroy();
      return;
    }
    if (selectedMode === "face") {
      saveVoiceConsent(persona.id as "budb" | "racalvin", false);
      setConsented(false);
      setStatus("fallback");
      setErr(null);
      destroy();
      return;
    }
    // voice
    setBusyAccept(true);
    setErr(null);
    try {
      saveVoiceConsent(persona.id as "budb" | "racalvin", true);
      setConsented(true);
      await startLive();
    } finally {
      setBusyAccept(false);
    }
  }, [destroy, persona.id, selectedMode, startLive]);

  const acceptVoice = useCallback(async () => {
    setSelectedMode("voice");
    setBusyAccept(true);
    setErr(null);
    try {
      saveVoiceConsent(persona.id as "budb" | "racalvin", true);
      setConsented(true);
      await startLive();
    } finally {
      setBusyAccept(false);
    }
  }, [persona.id, startLive]);

  // Returning visitors with consent: try Live when panel opens.
  // First-time open always uses the accept popup (user gesture required for mic).
  useEffect(() => {
    if (!autoStart || !open) return;
    if (!consented) {
      setStatus("gate");
      return;
    }
    if (startedRef.current) return;
    void startLive();
  }, [autoStart, open, consented, startLive]);

  // Tear down Live only when embed unmounts (not when consent flips mid-start)
  useEffect(() => {
    return () => destroy();
  }, [destroy]);

  useEffect(() => {
    const onChat = (ev: Event) => {
      const d = (ev as CustomEvent).detail as {
        text?: string;
        persona?: string;
      };
      const text = String(d?.text || "").trim();
      if (!text) return;
      const target = String(d?.persona || "budb").toLowerCase();
      if (target !== persona.id && !(persona.id === "budb" && !d?.persona)) {
        return;
      }
      ctrlRef.current?.sendText(text);
    };
    window.addEventListener(VIDU_CHAT_EVENT, onChat);
    window.addEventListener(BUDB_VIDU_CHAT_EVENT, onChat);
    return () => {
      window.removeEventListener(VIDU_CHAT_EVENT, onChat);
      window.removeEventListener(BUDB_VIDU_CHAT_EVENT, onChat);
    };
  }, [persona.id]);

  const showGate = status === "gate" || (!consented && status === "idle");

  // ── Minimized chip ──────────────────────────────────────────────────────
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          if (!consented) setStatus("gate");
        }}
        title={`Open ${persona.name} AI voice`}
        style={{
          position: pos === "relative" ? "relative" : "fixed",
          right: pos === "fixed" ? 12 : undefined,
          bottom:
            pos === "fixed"
              ? "max(72px, env(safe-area-inset-bottom))"
              : undefined,
          zIndex: 55,
          background: `linear-gradient(135deg,${theme.accent}40,#0a0a0f)`,
          border: `1px solid ${theme.border}`,
          borderRadius: 999,
          color: theme.title,
          fontSize: 12,
          fontWeight: 800,
          padding: "12px 16px",
          cursor: "pointer",
          boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
          minHeight: 44,
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <Maximize2 size={14} style={{ display: "inline", marginRight: 6 }} />
        {persona.handle} · AI voice
      </button>
    );
  }

  const statusLabel =
    status === "live"
      ? "LIVE · voice + face"
      : status === "gate"
        ? "Choose how to open AI voice"
        : status === "creating"
          ? "Opening microphone…"
          : status === "connecting"
            ? "Connecting Live AI…"
            : status === "fallback"
              ? consented
                ? "Face only · mic off"
                : "Face only · voice off"
              : status === "error"
                ? "Error"
                : "Ready";

  // ── Full-screen accept popup (required interaction) ─────────────────────
  const modal =
    showGate ? (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Enable ${persona.name} AI voice`}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 90,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding:
            "max(12px, env(safe-area-inset-top)) 14px max(16px, env(safe-area-inset-bottom))",
          background: "rgba(0,0,0,0.78)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            width: "min(420px, 100%)",
            maxHeight: "min(92dvh, 720px)",
            overflowY: "auto",
            borderRadius: 18,
            border: `1px solid ${theme.border}`,
            background:
              "linear-gradient(180deg, #16141c 0%, #0a0810 100%)",
            boxShadow: `0 24px 64px rgba(0,0,0,0.65), 0 0 40px ${theme.soft}`,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div
            style={{
              padding: "16px 18px 10px",
              background: theme.header,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: theme.accent,
                opacity: 0.9,
              }}
            >
              {persona.id === "racalvin"
                ? "Blackjack · AI dealer"
                : "HU VS BUDB · table buddy"}
            </div>
            <h2
              style={{
                margin: "6px 0 0",
                fontSize: "clamp(1.25rem, 4.5vw, 1.5rem)",
                fontWeight: 900,
                color: theme.title,
                letterSpacing: "0.03em",
                lineHeight: 1.15,
              }}
            >
              Talk with {persona.name}
            </h2>
            <p
              style={{
                margin: "8px 0 0",
                fontSize: 13,
                lineHeight: 1.45,
                color: "rgba(255,255,255,0.72)",
              }}
            >
              {persona.id === "racalvin"
                ? "Voice↔voice is only on this blackjack table. Pick how you want the AI dealer to open."
                : "Voice↔voice is only on HU VS BUDB (agent AI seat). Pick how you want BetUDontBet to open."}
            </p>
            {workerOk === false && (
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: 12,
                  color: "#fca5a5",
                }}
              >
                Voice service not configured on server — face-only still works.
              </p>
            )}
            {workerOk === true && (
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: 11,
                  color: "rgba(74,222,128,0.85)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Radio size={12} /> AI voice worker ready
              </p>
            )}
          </div>

          <div style={{ padding: "12px 14px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
            {options.map((opt) => {
              const on = selectedMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSelectedMode(opt.id)}
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    textAlign: "left",
                    width: "100%",
                    minHeight: 56,
                    padding: "12px 14px",
                    borderRadius: 14,
                    border: on
                      ? `2px solid ${theme.accent}`
                      : "1px solid rgba(255,255,255,0.1)",
                    background: on ? theme.soft : "rgba(0,0,0,0.35)",
                    color: "#f5f0e6",
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(0,0,0,0.4)",
                      color: theme.accent,
                    }}
                  >
                    {opt.icon === "mic" ? (
                      <Mic size={18} />
                    ) : opt.icon === "face" ? (
                      <MessageSquare size={18} />
                    ) : (
                      <MicOff size={18} />
                    )}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontWeight: 800,
                        fontSize: 14,
                        color: on ? theme.title : "#fff",
                      }}
                    >
                      {opt.title}
                    </span>
                    <span
                      style={{
                        display: "block",
                        marginTop: 3,
                        fontSize: 12,
                        lineHeight: 1.4,
                        color: "rgba(255,255,255,0.55)",
                      }}
                    >
                      {opt.desc}
                    </span>
                  </span>
                  <span
                    aria-hidden
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: on
                        ? `5px solid ${theme.accent}`
                        : "2px solid rgba(255,255,255,0.25)",
                      flexShrink: 0,
                      marginTop: 8,
                    }}
                  />
                </button>
              );
            })}
          </div>

          <div style={{ padding: "4px 16px 8px" }}>
            <ul
              style={{
                margin: 0,
                padding: "0 0 0 1.1rem",
                fontSize: 11,
                lineHeight: 1.4,
                color: "rgba(255,255,255,0.48)",
              }}
            >
              {tips.slice(0, platform.mobile ? 3 : 2).map((t) => (
                <li key={t.slice(0, 28)} style={{ marginBottom: 4 }}>
                  {t}
                </li>
              ))}
            </ul>
            <p
              style={{
                margin: "8px 0 0",
                fontSize: 10,
                color: "rgba(255,255,255,0.4)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <ShieldCheck size={12} color={theme.accent} />
              HTTPS · mic only after you confirm · Options → headset/mic anytime
            </p>
          </div>

          <div
            style={{
              padding: "10px 14px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <button
              type="button"
              disabled={busyAccept}
              onClick={() => void confirmOpen()}
              style={{
                minHeight: 48,
                borderRadius: 14,
                border: "none",
                background: theme.accent,
                color: "#0a0a0f",
                fontWeight: 900,
                fontSize: 15,
                letterSpacing: "0.03em",
                cursor: busyAccept ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {busyAccept ? (
                "Opening…"
              ) : selectedMode === "voice" ? (
                <>
                  <Volume2 size={18} /> Continue · enable voice
                </>
              ) : selectedMode === "face" ? (
                <>
                  <MessageSquare size={18} /> Continue · face only
                </>
              ) : (
                "Not now"
              )}
            </button>
            {selectedMode === "voice" && (
              <p
                style={{
                  margin: 0,
                  textAlign: "center",
                  fontSize: 11,
                  color: "rgba(255,255,255,0.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                <Headphones size={12} /> Browser will ask for microphone next
              </p>
            )}
          </div>
        </div>
      </div>
    ) : null;

  // ── Compact dock (after accept) ─────────────────────────────────────────
  return (
    <>
      {modal}
      <div
        className={props.className}
        style={{
          position: pos,
          right: pos === "fixed" ? (props.compact ? 8 : 14) : undefined,
          bottom:
            pos === "fixed"
              ? "max(88px, env(safe-area-inset-bottom))"
              : undefined,
          zIndex: pos === "fixed" ? 45 : 5,
          width: props.compact ? 148 : pos === "relative" ? "100%" : 220,
          maxWidth: pos === "relative" ? 300 : undefined,
          borderRadius: 14,
          overflow: "hidden",
          border: `1px solid ${theme.border}`,
          background: "rgba(8,8,12,0.96)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.55)",
          backdropFilter: "blur(10px)",
          opacity: showGate ? 0.35 : 1,
          pointerEvents: showGate ? "none" : "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 10px",
            background: theme.header,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 800, color: theme.title }}>
            <Radio
              size={12}
              style={{
                display: "inline",
                marginRight: 4,
                color: status === "live" ? "#4ade80" : "#a1a1aa",
              }}
            />
            {persona.name}
          </span>
          <span style={{ display: "flex", gap: 2, alignItems: "center" }}>
            <button
              type="button"
              title="Restart voice"
              onClick={() => void acceptVoice()}
              style={{
                background: "transparent",
                border: "none",
                color: "#a1a1aa",
                cursor: "pointer",
                padding: 4,
                minWidth: 28,
                minHeight: 28,
              }}
            >
              ↻
            </button>
            <a
              href={persona.shareUrl}
              target="_blank"
              rel="noreferrer"
              title="Open share avatar"
              style={{ color: "#a1a1aa", padding: 4 }}
            >
              <ExternalLink size={12} />
            </a>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                destroy();
              }}
              style={{
                background: "transparent",
                border: "none",
                color: "#a1a1aa",
                cursor: "pointer",
                padding: 4,
                minWidth: 28,
                minHeight: 28,
              }}
              aria-label="Minimize voice"
            >
              <Minimize2 size={12} />
            </button>
          </span>
        </div>

        <div
          style={{
            position: "relative",
            aspectRatio: "1 / 1.05",
            background: "#050508",
          }}
        >
          <iframe
            title={`${persona.name} avatar`}
            src={persona.shareUrl}
            allow="microphone; camera; autoplay; encrypted-media"
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={false}
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: "none",
            }}
          />
        </div>

        <div
          style={{
            padding: "8px 10px 10px",
            fontSize: 10,
            color: "#a1a1aa",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <span style={{ fontWeight: 700, color: "rgba(255,255,255,0.78)" }}>
            {statusLabel}
          </span>
          {liveId && (
            <span style={{ opacity: 0.65 }}>live {liveId.slice(0, 10)}…</span>
          )}
          {err && (
            <span style={{ color: "#fca5a5", lineHeight: 1.35 }} title={err}>
              {err.slice(0, 140)}
            </span>
          )}
          <span
            style={{
              opacity: 0.7,
              display: "flex",
              alignItems: "flex-start",
              gap: 4,
              lineHeight: 1.35,
            }}
          >
            <Headphones size={11} style={{ flexShrink: 0, marginTop: 1 }} />
            Talk to the face · or type chat · Options → mic/headset
          </span>
          {(status === "fallback" || status === "error" || status === "gate") && (
            <button
              type="button"
              onClick={() => {
                setStatus("gate");
                setSelectedMode("voice");
              }}
              style={{
                marginTop: 4,
                minHeight: 40,
                borderRadius: 10,
                border: `1px solid ${theme.border}`,
                background: theme.soft,
                color: theme.title,
                fontWeight: 800,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Open AI voice options
            </button>
          )}
          {(status === "fallback" || status === "error") && consented && (
            <button
              type="button"
              onClick={() => void acceptVoice()}
              style={{
                minHeight: 36,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(0,0,0,0.35)",
                color: "rgba(255,255,255,0.8)",
                fontWeight: 700,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Retry voice connection
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/** HU VS BUDB only */
export function BudbAvatarEmbed(props: {
  compact?: boolean;
  className?: string;
  autoStart?: boolean;
}) {
  return <ViduPersonaEmbed persona="budb" {...props} />;
}

/** Blackjack only */
export function RacalvinDealerEmbed(props: {
  compact?: boolean;
  className?: string;
  autoStart?: boolean;
  position?: "fixed" | "absolute" | "relative";
}) {
  return (
    <ViduPersonaEmbed
      persona="racalvin"
      position={props.position || "relative"}
      {...props}
    />
  );
}
